(() => {
  const RECOVERY_KEY = 'fs-active-product';
  const EVENT_NAME = 'fs:commerce';
  let pendingCartAction = null;

  const emitCommerce = (action, detail = {}) => {
    window.dispatchEvent(new CustomEvent(EVENT_NAME, {
      detail: {
        action,
        currency: detail.currency || window.Shopify?.currency?.active || null,
        ...detail,
      },
    }));
  };

  const validRecoveryUrl = (value) => {
    if (!value) return null;
    try {
      const url = new URL(value, window.location.origin);
      if (url.origin !== window.location.origin || !url.pathname.startsWith('/products/')) return null;
      return `${url.pathname}${url.search}`;
    } catch {
      return null;
    }
  };

  const rememberProduct = () => {
    const root = document.querySelector('[data-fs-product-url]');
    if (!root) return;
    const selectedVariantId = root.dataset.fsSelectedVariantId;
    const candidate = validRecoveryUrl(
      `${root.dataset.fsProductUrl}${selectedVariantId ? `?variant=${encodeURIComponent(selectedVariantId)}` : ''}`
    );
    if (candidate) sessionStorage.setItem(RECOVERY_KEY, candidate);
  };

  const updateRecoveryLinks = (root = document) => {
    const remembered = validRecoveryUrl(sessionStorage.getItem(RECOVERY_KEY));
    if (!remembered) return;
    root.querySelectorAll('[data-fs-empty-cart-recovery]').forEach((link) => {
      link.href = remembered;
    });
  };

  const setupRecovery = () => {
    rememberProduct();
    updateRecoveryLinks();
    new MutationObserver((records) => {
      records.forEach((record) => record.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) updateRecoveryLinks(node);
      }));
    }).observe(document.body, { childList: true, subtree: true });
  };

  const validateQuantity = (form) => {
    const input = form.elements.namedItem('quantity');
    if (!input) return true;
    const value = Number(input.value);
    const min = Number(input.min || 1);
    const max = input.max ? Number(input.max) : Infinity;
    const step = Number(input.step || 1);
    const valid = Number.isInteger(value) && value >= min && value <= max && (value - min) % step === 0;
    if (!valid) {
      input.reportValidity();
      input.focus();
    }
    return valid;
  };

  const setupProductForms = () => {
    document.addEventListener('submit', (event) => {
      if (!event.target.matches('[data-fs-product-form]')) return;
      if (!validateQuantity(event.target)) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }, true);

    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
      const variant = data?.variant;
      if (!variant) return;
      const root = document.querySelector(`[data-section-id="${CSS.escape(data.sectionId)}"]`)?.closest('[data-fs-product-url]');
      if (root) {
        root.dataset.fsSelectedVariantId = String(variant.id);
        root.dataset.fsSelectedPrice = String(variant.price ?? 0);
        sessionStorage.setItem(RECOVERY_KEY, `${root.dataset.fsProductUrl}?variant=${variant.id}`);
      }
      const availability = document.getElementById(`Inventory-${data.sectionId}`);
      availability?.classList.toggle('fs-availability--sold-out', !variant.available);
      const stickyPrice = root?.querySelector('[data-fs-sticky-price]');
      const stickySubmit = root?.querySelector('[data-fs-sticky-submit]');
      const stickyLabel = root?.querySelector('[data-fs-sticky-label]');
      if (stickyPrice && root.dataset.fsCurrency && Number.isFinite(Number(variant.price))) {
        stickyPrice.textContent = new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency: root.dataset.fsCurrency,
        }).format(Number(variant.price) / 100);
      }
      if (stickySubmit) stickySubmit.disabled = !variant.available;
      if (stickyLabel) stickyLabel.textContent = variant.available ? 'Add to cart' : 'Sold out';
    });

    subscribe(PUB_SUB_EVENTS.cartUpdate, ({ source, productVariantId, cartData }) => {
      if (cartData?.errors) return;
      if (source === 'product-form') {
        const form = document.querySelector('product-form form');
        const root = form?.closest('[data-fs-product-url]');
        const quantity = Number(form?.elements.quantity?.value || 1);
        const price = Number(root?.dataset.fsSelectedPrice || 0);
        emitCommerce('add_to_cart', {
          variantId: String(productVariantId || form?.elements.id?.value || ''),
          quantity,
          value: (price * quantity) / 100,
          currency: cartData?.currency || root?.dataset.fsCurrency,
        });
      } else if (source === 'cart-items' && pendingCartAction) {
        emitCommerce(pendingCartAction.action, {
          variantId: pendingCartAction.variantId,
          quantity: pendingCartAction.quantity,
          value: cartData?.total_price,
          currency: cartData?.currency,
        });
        pendingCartAction = null;
      }
    });
  };

  const setupGalleryProgress = () => {
    document.querySelectorAll('.fs-gallery').forEach((gallery) => {
      const progress = gallery.querySelector('[data-fs-gallery-progress]');
      const thumbs = [...gallery.querySelectorAll('.fs-gallery__thumb')];
      if (!progress || thumbs.length < 2) return;
      const dots = [...progress.querySelectorAll('.fs-gallery__progress-dots i')];
      const update = () => {
        const index = Math.max(0, thumbs.findIndex((thumb) => thumb.getAttribute('aria-current') === 'true'));
        progress.style.setProperty('--fs-gallery-index', String(index));
        dots.forEach((dot, dotIndex) => dot.classList.toggle('is-active', dotIndex === index));
      };
      new MutationObserver(update).observe(gallery, {
        subtree: true,
        attributes: true,
        attributeFilter: ['aria-current'],
      });
      update();
    });
  };

  const setupStickyPurchase = () => {
    document.querySelectorAll('[data-fs-product-url]').forEach((root) => {
      const strip = root.querySelector('[data-fs-mobile-purchase]');
      const layout = root.querySelector('.fs-product__layout');
      if (!strip || !layout) return;
      const observer = new IntersectionObserver(([entry]) => {
        const show = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
        strip.classList.toggle('is-visible', show);
      });
      observer.observe(layout);
    });
  };

  const setupCartEvents = () => {
    document.addEventListener('click', (event) => {
      const remove = event.target.closest('cart-remove-button');
      if (remove) {
        const row = remove.closest('.cart-item');
        pendingCartAction = {
          action: 'remove_from_cart',
          variantId: row?.querySelector('[data-quantity-variant-id]')?.dataset.quantityVariantId || null,
          quantity: 0,
        };
      }

      const checkout = event.target.closest('[data-fs-checkout-entry], #checkout, #CartDrawer-Checkout');
      if (checkout && !checkout.disabled) emitCommerce('checkout_entry', { value: checkout.dataset.cartValue });
    });

    document.addEventListener('change', (event) => {
      if (!event.target.matches('.cart-item .quantity__input')) return;
      pendingCartAction = {
        action: Number(event.target.value) === 0 ? 'remove_from_cart' : 'cart_update',
        variantId: event.target.dataset.quantityVariantId || null,
        quantity: Number(event.target.value),
      };
    });

    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    let wasOpen = drawer.classList.contains('active');
    new MutationObserver(() => {
      const isOpen = drawer.classList.contains('active');
      if (isOpen && !wasOpen) emitCommerce('cart_view');
      wasOpen = isOpen;
    }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
  };

  const init = () => {
    setupRecovery();
    setupProductForms();
    setupGalleryProgress();
    setupStickyPurchase();
    setupCartEvents();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
